import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import workforceManagementService from '../../services/workforceManagementService';
import employeeService from '../../services/employeeService';

const BulkEmployeeImport = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [error, setError] = useState('');
  const [globalFilter, setGlobalFilter] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [limitValidation, setLimitValidation] = useState(null);

  const requiredFields = [
    'employeeNumber',
    'firstName',
    'lastName',
    'email',
    'department',
    'position',
    'hireDate'
  ];

  const fieldMapping = {
    'Employee Number': 'employeeNumber',
    'First Name': 'firstName',
    'Last Name': 'lastName',
    'Email': 'email',
    'Phone': 'phone',
    'Date of Birth': 'dateOfBirth',
    'Employment Type': 'employmentType',
    'Citizenship Status': 'citizenStatus',
    'Department': 'department',
    'Position': 'position',
    'Hire Date': 'hireDate'
  };

  const handleFileSelect = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setParsedData([]);
    setValidationErrors([]);
    setImportResults(null);
    setError('');
  };

  const parseFile = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setLoading(true);
    setError('');
    setParsedData([]);
    setValidationErrors([]);

    try {
      const fileExtension = file?.name?.split('.')?.pop()?.toLowerCase();

      if (fileExtension === 'csv') {
        parseCSV(file);
      } else if (['xlsx', 'xls']?.includes(fileExtension)) {
        parseExcel(file);
      } else {
        setError('Unsupported file format. Please upload CSV or Excel files.');
        setLoading(false);
      }
    } catch (err) {
      setError(err?.message || 'Failed to parse file');
      setLoading(false);
    }
  };

  const parseCSV = (file) => {
    Papa?.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        processData(results?.data);
        setLoading(false);
      },
      error: (err) => {
        setError('Failed to parse CSV file: ' + err?.message);
        setLoading(false);
      }
    });
  };

  const parseExcel = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e?.target?.result);
        const workbook = XLSX?.read(data, { type: 'array' });
        const firstSheet = workbook?.Sheets?.[workbook?.SheetNames?.[0]];
        const jsonData = XLSX?.utils?.sheet_to_json(firstSheet);
        processData(jsonData);
        setLoading(false);
      } catch (err) {
        setError('Failed to parse Excel file: ' + err?.message);
        setLoading(false);
      }
    };
    reader.onerror = () => {
      setError('Failed to read file');
      setLoading(false);
    };
    reader?.readAsArrayBuffer(file);
  };

  const processData = (data) => {
    const normalized = data?.map((row, index) => {
      const normalizedRow = { rowNumber: index + 2 }; // +2 for header and 1-based index
      
      Object.keys(row)?.forEach(key => {
        const mappedKey = fieldMapping?.[key] || key;
        normalizedRow[mappedKey] = row?.[key];
      });

      return normalizedRow;
    });

    const errors = validateData(normalized);
    setParsedData(normalized);
    setValidationErrors(errors);
  };

  const validateData = (data) => {
    const errors = [];

    data?.forEach((row, index) => {
      const rowErrors = [];

      requiredFields?.forEach(field => {
        if (!row?.[field] || String(row?.[field])?.trim() === '') {
          rowErrors?.push(`Missing ${field}`);
        }
      });

      // Email validation
      if (row?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(row?.email)) {
        rowErrors?.push('Invalid email format');
      }

      // Employment type validation
      const validEmploymentTypes = ['full_time', 'casual', 'fixed_term', 'non_citizen'];
      if (row?.employmentType && !validEmploymentTypes?.includes(row?.employmentType?.toLowerCase())) {
        rowErrors?.push('Invalid employment type (must be: full_time, casual, fixed_term, non_citizen)');
      }

      // Citizenship status validation
      const validCitizenStatus = ['citizen', 'non_citizen'];
      if (row?.citizenStatus && !validCitizenStatus?.includes(row?.citizenStatus?.toLowerCase())) {
        rowErrors?.push('Invalid citizenship status (must be: citizen, non_citizen)');
      }

      if (rowErrors?.length > 0) {
        errors?.push({
          row: row?.rowNumber || index + 1,
          errors: rowErrors
        });
      }
    });

    return errors;
  };

  const handleImport = async () => {
    if (validationErrors?.length > 0) {
      setError('Please fix validation errors before importing');
      return;
    }

    if (parsedData?.length === 0) {
      setError('No data to import');
      return;
    }

    setImporting(true);
    setError('');

    try {
      const company = await workforceManagementService?.getActiveCompany();
      
      if (!company?.id) {
        throw new Error('No active company found');
      }

      // Validate employee limit before import
      const validation = await employeeService?.validateEmployeeLimit(
        company?.id,
        parsedData?.length
      );

      if (!validation?.allowed) {
        setLimitValidation(validation);
        setShowUpgradeModal(true);
        setImporting(false);
        return;
      }

      const results = await workforceManagementService?.bulkAddEmployees(
        company?.id,
        parsedData
      );

      setImportResults(results);
      
      if (results?.successCount > 0) {
        setTimeout(() => {
          navigate('/workforce-management');
        }, 3000);
      }
    } catch (err) {
      setError(err?.message || 'Failed to import employees');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = [
      {
        'Employee Number': 'EMP001',
        'First Name': 'John',
        'Last Name': 'Doe',
        'Email': 'john.doe@company.com',
        'Phone': '+675 1234 5678',
        'Date of Birth': '1990-01-15',
        'Employment Type': 'full_time',
        'Citizenship Status': 'citizen',
        'Department': 'Human Resources',
        'Position': 'HR Manager',
        'Hire Date': '2024-01-01'
      }
    ];

    const ws = XLSX?.utils?.json_to_sheet(template);
    const wb = XLSX?.utils?.book_new();
    XLSX?.utils?.book_append_sheet(wb, ws, 'Employee Template');
    XLSX?.writeFile(wb, 'employee_import_template.xlsx');
  };

  const resetImport = () => {
    setFile(null);
    setParsedData([]);
    setValidationErrors([]);
    setImportResults(null);
    setError('');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <GlobalFilterBar onFilterChange={setGlobalFilter} />
      <ComplianceAlertBanner />
      <DataRefreshIndicator />
      <main className="container mx-auto px-6 py-8 mt-16">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Bulk Employee Import</h1>
              <p className="text-muted-foreground">Upload CSV or Excel files to import multiple employees at once</p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/workforce-management')}
            >
              <Icon name="ArrowLeft" size={16} className="mr-2" />
              Back to Workforce
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-6">
            <p className="font-medium">Error</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {importResults && (
          <div className="bg-success/10 border border-success text-success px-4 py-3 rounded-md mb-6">
            <p className="font-medium">Import Complete</p>
            <p className="text-sm">
              Successfully imported {importResults?.successCount} employees.
              {importResults?.failedCount > 0 && ` ${importResults?.failedCount} failed.`}
            </p>
            <p className="text-sm mt-2">Redirecting to workforce management...</p>
          </div>
        )}

        {/* Upload Section */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-foreground mb-2">Step 1: Upload File</h2>
              <p className="text-sm text-muted-foreground">Select a CSV or Excel file containing employee data</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadTemplate}
            >
              <Icon name="Download" size={16} className="mr-2" />
              Download Template
            </Button>
          </div>

          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
            <Icon name="Upload" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button
                variant="outline"
                as="span"
                className="cursor-pointer"
              >
                <Icon name="FileUp" size={16} className="mr-2" />
                Choose File
              </Button>
            </label>
            {file && (
              <p className="text-sm text-foreground mt-4">
                Selected: <span className="font-medium">{file?.name}</span>
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Supported formats: CSV, XLSX, XLS
            </p>
          </div>

          {file && (
            <div className="flex gap-3 mt-6">
              <Button
                variant="default"
                onClick={parseFile}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                    Parsing...
                  </>
                ) : (
                  <>
                    <Icon name="FileSearch" size={16} className="mr-2" />
                    Parse & Validate
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={resetImport}
                disabled={loading || importing}
              >
                <Icon name="RotateCcw" size={16} className="mr-2" />
                Reset
              </Button>
            </div>
          )}
        </div>

        {/* Validation Results */}
        {parsedData?.length > 0 && (
          <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
            <h2 className="text-xl font-bold text-foreground mb-4">Step 2: Review & Validate</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-background rounded-lg p-4 border border-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Rows</span>
                  <Icon name="FileText" size={20} className="text-primary" />
                </div>
                <p className="text-2xl font-bold text-foreground mt-2">{parsedData?.length}</p>
              </div>
              <div className="bg-background rounded-lg p-4 border border-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Valid Rows</span>
                  <Icon name="CheckCircle" size={20} className="text-success" />
                </div>
                <p className="text-2xl font-bold text-success mt-2">
                  {parsedData?.length - validationErrors?.length}
                </p>
              </div>
              <div className="bg-background rounded-lg p-4 border border-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Errors</span>
                  <Icon name="AlertCircle" size={20} className="text-error" />
                </div>
                <p className="text-2xl font-bold text-error mt-2">{validationErrors?.length}</p>
              </div>
            </div>

            {validationErrors?.length > 0 && (
              <div className="bg-error/10 border border-error rounded-lg p-4 mb-6">
                <div className="flex items-start gap-2 mb-3">
                  <Icon name="AlertTriangle" size={20} className="text-error mt-0.5" />
                  <div>
                    <p className="font-medium text-error">Validation Errors Found</p>
                    <p className="text-sm text-error/80">Please fix the following errors before importing:</p>
                  </div>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {validationErrors?.map((error, index) => (
                    <div key={index} className="bg-background rounded p-3 text-sm">
                      <p className="font-medium text-foreground mb-1">Row {error?.row}:</p>
                      <ul className="list-disc list-inside text-muted-foreground">
                        {error?.errors?.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Data Preview */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-foreground mb-3">Data Preview</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Row</th>
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Employee #</th>
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Name</th>
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Email</th>
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Department</th>
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Position</th>
                      <th className="text-left py-2 px-3 text-muted-foreground font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData?.slice(0, 10)?.map((row, index) => {
                      const hasError = validationErrors?.some(e => e?.row === row?.rowNumber);
                      return (
                        <tr key={index} className={`border-b border-border ${hasError ? 'bg-error/5' : ''}`}>
                          <td className="py-2 px-3 text-foreground">{row?.rowNumber}</td>
                          <td className="py-2 px-3 text-foreground">{row?.employeeNumber}</td>
                          <td className="py-2 px-3 text-foreground">
                            {row?.firstName} {row?.lastName}
                          </td>
                          <td className="py-2 px-3 text-foreground">{row?.email}</td>
                          <td className="py-2 px-3 text-foreground">{row?.department}</td>
                          <td className="py-2 px-3 text-foreground">{row?.position}</td>
                          <td className="py-2 px-3">
                            {hasError ? (
                              <span className="inline-flex items-center text-error">
                                <Icon name="XCircle" size={16} className="mr-1" />
                                Error
                              </span>
                            ) : (
                              <span className="inline-flex items-center text-success">
                                <Icon name="CheckCircle" size={16} className="mr-1" />
                                Valid
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {parsedData?.length > 10 && (
                  <p className="text-sm text-muted-foreground mt-3 text-center">
                    Showing 10 of {parsedData?.length} rows
                  </p>
                )}
              </div>
            </div>

            {validationErrors?.length === 0 && (
              <Button
                variant="default"
                size="lg"
                onClick={handleImport}
                disabled={importing}
                className="w-full"
              >
                {importing ? (
                  <>
                    <Icon name="Loader2" size={20} className="mr-2 animate-spin" />
                    Importing {parsedData?.length} Employees...
                  </>
                ) : (
                  <>
                    <Icon name="Upload" size={20} className="mr-2" />
                    Import {parsedData?.length} Employees
                  </>
                )}
              </Button>
            )}
          </div>
        )}

        {/* Upgrade Modal */}
        {showUpgradeModal && limitValidation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-lg shadow-xl max-w-lg w-full">
              <div className="flex items-center justify-between p-6 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="bg-warning/10 p-2 rounded-lg">
                    <Icon name="AlertTriangle" size={24} className="text-warning" />
                  </div>
                  <h2 className="text-xl font-bold text-foreground">Employee Limit Reached</h2>
                </div>
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <div className="p-6">
                <div className="bg-error/10 border border-error rounded-lg p-4 mb-6">
                  <p className="text-sm text-error">{limitValidation?.error}</p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="bg-background rounded-lg p-4 border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Current Plan</span>
                      <span className="text-sm font-medium text-foreground">
                        {limitValidation?.currentTier?.replace(/_/g, ' ')?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Current Employees</span>
                      <span className="text-sm font-medium text-foreground">
                        {limitValidation?.currentCount} / {limitValidation?.maxAllowed}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Monthly Price</span>
                      <span className="text-sm font-medium text-foreground">
                        K{limitValidation?.currentTierPrice?.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {limitValidation?.nextTier && (
                    <div className="bg-primary/10 rounded-lg p-4 border border-primary">
                      <div className="flex items-center gap-2 mb-3">
                        <Icon name="TrendingUp" size={16} className="text-primary" />
                        <span className="text-sm font-semibold text-primary">Recommended Upgrade</span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Plan</span>
                        <span className="text-sm font-medium text-foreground">
                          {limitValidation?.nextTier?.name?.replace(/_/g, ' ')?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Max Employees</span>
                        <span className="text-sm font-medium text-foreground">
                          Up to {limitValidation?.nextTier?.maxEmployees}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Monthly Price</span>
                        <span className="text-sm font-medium text-foreground">
                          K{limitValidation?.nextTier?.price?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowUpgradeModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => navigate('/billing-subscription-management')}
                    className="flex-1"
                  >
                    <Icon name="CreditCard" size={16} className="mr-2" />
                    Upgrade Plan
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <h2 className="text-xl font-bold text-foreground mb-4">Import Instructions</h2>
          <div className="space-y-3 text-sm text-muted-foreground">
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle" size={16} className="text-success mt-0.5" />
              <p><span className="font-medium text-foreground">Required Fields:</span> Employee Number, First Name, Last Name, Email, Department, Position, Hire Date</p>
            </div>
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle" size={16} className="text-success mt-0.5" />
              <p><span className="font-medium text-foreground">Optional Fields:</span> Phone, Date of Birth, Employment Type, Citizenship Status</p>
            </div>
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle" size={16} className="text-success mt-0.5" />
              <p><span className="font-medium text-foreground">Employment Types:</span> full_time, casual, fixed_term, non_citizen</p>
            </div>
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle" size={16} className="text-success mt-0.5" />
              <p><span className="font-medium text-foreground">Citizenship Status:</span> citizen, non_citizen</p>
            </div>
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle" size={16} className="text-success mt-0.5" />
              <p><span className="font-medium text-foreground">Date Format:</span> YYYY-MM-DD (e.g., 2024-01-15)</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BulkEmployeeImport;